import { useState, useEffect, useCallback, useMemo, useRef, memo } from "react";
import axios from "axios";
import "./styles/LightControl.css";
import Clock from "../../components/Clock";
import search from "/assets/icon/Search.svg";
import filter from "/assets/icon/Filter.svg";
import Edit from "/assets/icon/Edit.png";
import lightControlOff from "/lightControlOff.png";
import Map from "../../components/Map";
import ArcGauge from "../../components/ArcGauge";
import UserStatus from "../../components/UserStatus";
import debounce from "lodash/debounce";

const TableRow = memo(({ row, userPole, controllabilitiesData, openModal }) => {
  return (
    <tr>
      {controllabilitiesData && controllabilitiesData.light_bulb !== 2 && (
        <td>
          {userPole && userPole.light_bulb === 2 ? (
            <img
              src={lightControlOff}
              alt="Light Control Off"
              style={{ cursor: "not-allowed" }}
            />
          ) : (
            <img
              src={Edit}
              onClick={() => openModal(row)}
              style={{ cursor: "pointer" }}
              alt="Edit"
            />
          )}
        </td>
      )}
      <td>{row.code}</td>
      <td>{row.name}</td>
      <td>{row.location}</td>
      <td>{`${row.lat}, ${row.lng}`}</td>
      <td></td>
      <td>
        <UserStatus isOnline={row.active} />
      </td>
      <td></td>
    </tr>
  );
});

const LightControl = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isAllLightsModalOpen, setIsAllLightsModalOpen] = useState(false);
  const [selectedLight, setSelectedLight] = useState(null);
  const [rows, setRows] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [gaugeValue, setGaugeValue] = useState(0);
  const [controllabilitiesData, setControllabilitiesData] = useState(null);
  const [userPolesData, setUserPolesData] = useState([]);
  const [allSelected, setAllSelected] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isFromAdjustAll, setIsFromAdjustAll] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState("");
  const [isSuccess, setIsSuccess] = useState(true);
  const originalGaugeValue = useRef(0);
  const rowsPerPage = 5;

  const debouncedSearch = useCallback(
    debounce((term) => {
      setSearchTerm(term);
    }, 300),
    []
  );

  const fetchInitialData = useCallback(async () => {
    setIsLoading(true);
    const branchName = sessionStorage.getItem("selectedBranch");
    const userData = JSON.parse(sessionStorage.getItem("userData"));

    if (!branchName || !userData) {
      setIsLoading(false);
      return;
    }

    try {
      const [polesRes, controllabilitiesRes] = await Promise.all([
        axios.get(
          `${import.meta.env.VITE_API_URL}/api/branch-poles/${branchName}`
        ),
        axios.get(
          `${import.meta.env.VITE_API_URL}/controllabilities/${
            userData.id
          }/${branchName}`
        ),
      ]);

      setRows(polesRes.data);
      setSelectedRows(new Array(polesRes.data.length).fill(false));

      if (controllabilitiesRes.data.length > 0) {
        setControllabilitiesData(controllabilitiesRes.data[0]);

        if (
          controllabilitiesRes.data[0].light_bulb === 1 ||
          controllabilitiesRes.data[0].light_bulb === 3
        ) {
          const userPolesRes = await axios.get(
            `${import.meta.env.VITE_API_URL}/user-poles/${userData.id}`
          );
          setUserPolesData(userPolesRes.data);
        }
      }
    } catch (error) {
      console.error("Error fetching initial data:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  useEffect(() => {
    sessionStorage.setItem("gaugeValue", gaugeValue);
  }, [gaugeValue]);

  const openModal = (light) => {
    setSelectedLight(light);
    setIsFromAdjustAll(false);
    fetchGaugeValue(light.id);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setGaugeValue(originalGaugeValue.current);
    setSelectedLight(null);
    setIsModalOpen(false);
  };

  const openConfirmModal = () => setIsConfirmModalOpen(true);
  const closeConfirmModal = () => setIsConfirmModalOpen(false);
  const openAllLightsModal = () => {
    setIsFromAdjustAll(true);
    setIsAllLightsModalOpen(true);
  };
  const closeAllLightsModal = () => setIsAllLightsModalOpen(false);

  const fetchGaugeValue = async (poleId) => {
    try {
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}/latest-audit/${poleId}`
      );
      const fetchedValue = response.data?.payload?.value || 0;
      setGaugeValue(fetchedValue);
      originalGaugeValue.current = fetchedValue;
    } catch (error) {
      console.error("Error fetching gauge value:", error);
      setGaugeValue(0);
      originalGaugeValue.current = 0;
    }
  };

  const handleGaugeValueChange = useCallback((newValue) => {
    setGaugeValue(newValue);
  }, []);

  const handleConfirmSinglePole = useCallback(async () => {
    setIsLoading(true);
    try {
      const userData = JSON.parse(sessionStorage.getItem("userData"));
      if (!userData || !selectedLight) {
        throw new Error("Missing required data");
      }

      const payload = {
        user_id: userData.id,
        pole_id: selectedLight.id,
        command_type: "dim_light",
        payload: {
          hw_id: selectedLight.hardware_id,
          value: gaugeValue,
          cmd_type: "dim_light",
        },
      };

      // Send request with timeout
      await axios.post(
        `${import.meta.env.VITE_API_URL}/control-audits`,
        payload,
        { timeout: 10000 }
      );

      // Save to session storage
      const dateTime = new Date().toLocaleString();
      const previousRecords =
        JSON.parse(sessionStorage.getItem("auditRecords")) || [];
      const newRecord = {
        timestamp: dateTime,
        payload,
        value: gaugeValue,
      };
      sessionStorage.setItem(
        "auditRecords",
        JSON.stringify([...previousRecords, newRecord])
      );

      // Non-blocking line notification
      const message = `${dateTime} by ${userData.name}\nDim = ${gaugeValue}% # pole_code: ${selectedLight.code}`;
      sendLineNotification(message);

      setNotificationMessage("ปรับไฟสำเร็จ");
      setIsSuccess(true);
      setShowNotification(true);

      setTimeout(() => {
        setShowNotification(false);
        closeConfirmModal();
        closeModal();
      }, 5000);
    } catch (error) {
      console.error("Error updating light:", error);
      setNotificationMessage("เกิดข้อผิดพลาด กรุณาลองใหม่");
      setIsSuccess(false);
      setShowNotification(true);
    } finally {
      setIsLoading(false);
    }
  }, [gaugeValue, selectedLight]);

  const handleConfirmMultiplePoles = useCallback(async () => {
    setIsLoading(true);
    try {
      const userData = JSON.parse(sessionStorage.getItem("userData"));
      if (!userData) {
        throw new Error("User data not found");
      }

      const selectedPoles = rows.filter((_, index) => selectedRows[index]);
      if (selectedPoles.length === 0) {
        throw new Error("No poles selected");
      }

      const payloads = selectedPoles.map((pole) => ({
        user_id: userData.id,
        pole_id: pole.id,
        command_type: "dim_light",
        payload: {
          hw_id: pole.hardware_id,
          value: gaugeValue,
          cmd_type: "dim_light",
        },
      }));

      // Send requests in parallel
      await Promise.all(
        payloads.map((payload) =>
          axios.post(`${import.meta.env.VITE_API_URL}/control-audits`, payload)
        )
      );

      // Save to session storage
      const dateTime = new Date().toLocaleString();
      const previousRecords =
        JSON.parse(sessionStorage.getItem("auditRecords")) || [];
      const newRecords = selectedPoles.map((pole) => ({
        timestamp: dateTime,
        payload: {
          user_id: userData.id,
          pole_id: pole.id,
          command_type: "dim_light",
          payload: {
            hw_id: pole.hardware_id,
            value: gaugeValue,
            cmd_type: "dim_light",
          },
        },
        value: gaugeValue,
      }));
      sessionStorage.setItem(
        "auditRecords",
        JSON.stringify([...previousRecords, ...newRecords])
      );

      // Send Line notification
      const message =
        `${dateTime}\nby: ${userData.name}\nDim = ${gaugeValue}%\n` +
        selectedPoles
          .map((pole, i) => `${i + 1}: pole_code: ${pole.code}`)
          .join("\n");

      sendLineNotification(message);

      // Reset states & show notification
      setSelectedRows(new Array(rows.length).fill(false));
      setAllSelected(false);
      setNotificationMessage("ปรับไฟสำเร็จ");
      setIsSuccess(true);
      setShowNotification(true);

      setTimeout(() => {
        setShowNotification(false);
        closeConfirmModal();
        closeAllLightsModal();
      }, 5000);
    } catch (error) {
      console.error("Error updating lights:", error);
      setNotificationMessage("เกิดข้อผิดพลาด กรุณาลองใหม่");
      setIsSuccess(false);
      setShowNotification(true);
    } finally {
      setIsLoading(false);
    }
  }, [rows, selectedRows, gaugeValue]);

  const handleSelectAll = (e) => {
    const isChecked = e.target.checked;
    setAllSelected(isChecked);

    const updatedSelectedRows = rows.map((row) => {
      const userPole = userPolesData.find(
        (pole) => pole.controllable_id === row.id
      );
      return isChecked && !(userPole && userPole.light_bulb === 2);
    });

    setSelectedRows(updatedSelectedRows);
  };

  const handleCheckboxChange = (index) => {
    const updatedSelectedRows = [...selectedRows];
    updatedSelectedRows[index] = !updatedSelectedRows[index];
    setSelectedRows(updatedSelectedRows);
    setAllSelected(rows.every((_, i) => updatedSelectedRows[i]));
  };

  const filteredRows = useMemo(() => {
    if (!searchTerm) return rows;
    const term = searchTerm.toLowerCase();
    return rows
      .filter(
        (row) =>
          !row.isHidden &&
          !row.isDeleted &&
          (row.name.toLowerCase().includes(term) ||
            row.code.toLowerCase().includes(term) ||
            row.location.toLowerCase().includes(term))
      )
      .sort((a, b) => b.isMaster - a.isMaster);
  }, [rows, searchTerm]);

  const mainPolesCount = useMemo(
    () => filteredRows.filter((row) => row.is_master).length,
    [filteredRows]
  );

  const subPolesCount = useMemo(
    () => filteredRows.filter((row) => !row.is_master).length,
    [filteredRows]
  );

  const handleNextPage = () => {
    if (currentPage < Math.ceil(filteredRows.length / rowsPerPage)) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = filteredRows.slice(indexOfFirstRow, indexOfLastRow);
  const selectedCount = selectedRows.filter(Boolean).length;

  const sendLineNotification = async (message) => {
    try {
      await axios.post(
        `${import.meta.env.VITE_API_URL}/notify`,
        { message },
        {
          headers: { "Content-Type": "application/json" },
          timeout: 15000,
        }
      );
    } catch (error) {
      console.error("Error sending notification:", error);
    }
  };

  return (
    <div className="containerPage">
      <div className="sections1">
        <h1>ควบคุมหลอดไฟ</h1>
        <h2>
          <Clock />
        </h2>
      </div>

      <div className="areaMap-lightpage">
        <Map />
      </div>

      <div className="section3">
        <div className="led-section">
          <div className="search-bar">
            <img src={search} alt="search" className="search-user" />
            <input
              type="text"
              placeholder="ค้นหารหัสเสา, ชื่อเสา, สถานที่ตั้ง"
              onChange={(e) => debouncedSearch(e.target.value)}
            />
            <div>
              <img src={filter} alt="filter" className="filter-user" />
            </div>
          </div>

          <div className="countPole">
            <label>
              เสาหลัก <strong>{mainPolesCount}</strong> เสา || เสารอง{" "}
              <strong>{subPolesCount}</strong> เสา
            </label>
            {controllabilitiesData &&
              controllabilitiesData.light_bulb !== 2 && (
                <strong
                  className="adjust-all-lights-button"
                  onClick={openAllLightsModal}
                >
                  ปรับค่าความสว่างทั้งหมด
                </strong>
              )}
          </div>

          {isLoading ? (
            <div className="loading-container">
              <div className="lds-spinner">
                {[...Array(7)].map((_, i) => (
                  <div key={i}></div>
                ))}
              </div>
            </div>
          ) : (
            <table className="tableCustom">
              <thead>
                <tr>
                  {controllabilitiesData &&
                    controllabilitiesData.light_bulb !== 2 && <th>ปรับไฟ</th>}
                  <th>รหัสเสา</th>
                  <th>ชื่อเสา</th>
                  <th>สถานที่ตั้ง</th>
                  <th>พิกัด</th>
                  <th></th>
                  <th className="px50">สถานะเสา</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.length === 0 ? (
                  <tr>
                    <td colSpan="8">ไม่พบข้อมูล</td>
                  </tr>
                ) : (
                  currentRows.map((row, index) => {
                    const userPole = userPolesData.find(
                      (pole) => pole.controllable_id === row.id
                    );
                    return (
                      <TableRow
                        key={index}
                        row={row}
                        userPole={userPole}
                        controllabilitiesData={controllabilitiesData}
                        openModal={openModal}
                      />
                    );
                  })
                )}
              </tbody>
            </table>
          )}

          <div className="pagination">
            <button
              onClick={handlePreviousPage}
              disabled={currentPage === 1 || isLoading}
            >
              ◂
            </button>
            <span>
              {currentPage} of {Math.ceil(filteredRows.length / rowsPerPage)}
            </span>
            <button
              onClick={handleNextPage}
              disabled={
                currentPage === Math.ceil(filteredRows.length / rowsPerPage) ||
                isLoading
              }
            >
              ▸
            </button>
          </div>
        </div>
      </div>

      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            {isLoading ? (
              <div className="centerLoad">
                <div className="lds-spinner">
                  {[...Array(7)].map((_, i) => (
                    <div key={i}></div>
                  ))}
                </div>
              </div>
            ) : (
              <>
                <button className="close-button" onClick={closeModal}>
                  &times;
                </button>
                <h2>ปรับความสว่างรายเสา</h2>
                <div className="settingLightcenter">
                  <div className="settingLight">
                    <ArcGauge
                      poleId={selectedLight.id}
                      onValueChange={handleGaugeValueChange}
                    />
                  </div>
                </div>
                <strong>รายละเอียดเสา</strong>
                <div className="detaiLlight">
                  <label>รหัสเสา</label>
                  <label>ชื่อเสา</label>
                  <strong>{selectedLight.code}</strong>
                  <strong>{selectedLight.name}</strong>
                  <label>ประเภทเสา</label>
                  <label>ตำแหน่งติดตั้ง</label>
                  <strong>
                    {selectedLight.isMaster ? "เสาหลัก" : "เสารอง"}
                  </strong>
                  <strong>{selectedLight.location}</strong>
                  <label>ละติจูด</label>
                  <label>ลองจิจูด</label>
                  <strong>{selectedLight.lat}</strong>
                  <strong>{selectedLight.lng}</strong>
                </div>

                <div className="button-adduser-detali">
                  <button
                    className="cancel-button"
                    onClick={closeModal}
                    disabled={isLoading}
                  >
                    ยกเลิก
                  </button>
                  <button
                    className="confirm-button"
                    onClick={openConfirmModal}
                    disabled={isLoading}
                  >
                    ตกลง
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {isAllLightsModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            {isLoading ? (
              <div className="lds-spinner">
                {[...Array(7)].map((_, i) => (
                  <div key={i}></div>
                ))}
              </div>
            ) : (
              <>
                <button
                  className="close-button"
                  onClick={closeAllLightsModal}
                  disabled={isLoading}
                >
                  &times;
                </button>
                <h2>ปรับความสว่างหลอดไฟ</h2>
                <div className="sticky-gauge-container">
                  <div className="settingLightcenter sticky-gauge">
                    <div className="settingLight">
                      <ArcGauge onValueChange={handleGaugeValueChange} />
                    </div>
                  </div>
                </div>
                <div className="colectLightControl">
                  <table className="tableCustom">
                    <thead>
                      <tr>
                        {controllabilitiesData &&
                          controllabilitiesData.light_bulb !== 2 && (
                            <th>
                              <input
                                type="checkbox"
                                className="custom-checkbox"
                                id="select-all"
                                onChange={handleSelectAll}
                                checked={allSelected}
                                disabled={isLoading}
                              />
                            </th>
                          )}
                        <th>รหัสเสา</th>
                        <th>ชื่อเสา</th>
                        <th>สถานที่ตั้ง</th>
                        <th>พิกัด</th>
                        <th className="status-column">สถานะเสา</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentRows.length === 0 ? (
                        <tr>
                          <td colSpan="6">ไม่พบข้อมูล</td>
                        </tr>
                      ) : (
                        currentRows.map((row, index) => {
                          const userPole = userPolesData.find(
                            (pole) => pole.controllable_id === row.id
                          );
                          return (
                            <tr key={index}>
                              {!(userPole && userPole.light_bulb === 2) && (
                                <>
                                  <td>
                                    <input
                                      type="checkbox"
                                      className="custom-checkbox"
                                      checked={
                                        selectedRows[indexOfFirstRow + index]
                                      }
                                      onChange={() =>
                                        handleCheckboxChange(
                                          indexOfFirstRow + index
                                        )
                                      }
                                      disabled={isLoading}
                                    />
                                  </td>
                                  <td>{row.code}</td>
                                  <td>{row.name}</td>
                                  <td>{row.location}</td>
                                  <td>{`${row.lat}, ${row.lng}`}</td>
                                  <td className="status-column">
                                    <UserStatus isOnline={row.active} />
                                  </td>
                                </>
                              )}
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                  <div className="pagination">
                    <button
                      onClick={handlePreviousPage}
                      disabled={currentPage === 1 || isLoading}
                    >
                      ◂
                    </button>
                    <span>
                      {currentPage} of{" "}
                      {Math.ceil(filteredRows.length / rowsPerPage)}
                    </span>
                    <button
                      onClick={handleNextPage}
                      disabled={
                        currentPage ===
                          Math.ceil(filteredRows.length / rowsPerPage) ||
                        isLoading
                      }
                    >
                      ▸
                    </button>
                  </div>
                  <div className="selected-count">
                    เลือกเสาแล้ว <span>{selectedCount}</span> เสา
                  </div>
                </div>
                <div className="button-adduser-detali">
                  <button
                    className="cancel-button"
                    onClick={closeAllLightsModal}
                    disabled={isLoading}
                  >
                    ยกเลิก
                  </button>
                  <button
                    className="confirm-button"
                    onClick={() => setIsConfirmModalOpen(true)}
                    disabled={isLoading}
                  >
                    ตกลง
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {isConfirmModalOpen && (
        <div className="modal-overlay" onClick={closeConfirmModal}>
          <div className="modal-content-confeirm">
            <button
              className="close-button"
              onClick={closeConfirmModal}
              disabled={isLoading}
            >
              &times;
            </button>
            <h2>ยืนยันการปรับความสว่าง</h2>
            {isFromAdjustAll && <h2>จำนวนเสา {selectedCount} รายการ</h2>}
            <h2>ความสว่าง {gaugeValue} %</h2>
            {isLoading ? (
              <div className="loading-container">
                <div className="lds-spinner">
                  {[...Array(7)].map((_, i) => (
                    <div key={i}></div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="button-adduser-detali">
                <button
                  className="cancel-button"
                  onClick={closeConfirmModal}
                  disabled={isLoading}
                >
                  ยกเลิก
                </button>
                <button
                  className="confirm-button"
                  onClick={
                    isFromAdjustAll
                      ? handleConfirmMultiplePoles
                      : handleConfirmSinglePole
                  }
                  disabled={isLoading}
                >
                  ตกลง
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {showNotification && (
        <div className="notification-overlay">
          <div
            className={`notification-content ${
              isSuccess ? "success" : "error"
            }`}
          >
            {notificationMessage}
          </div>
        </div>
      )}
    </div>
  );
};

export default LightControl;
